import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import CustomStore from 'devextreme/data/custom_store';
import notify from 'devextreme/ui/notify';
import {
  DataGrid,
  Column,
  SearchPanel,
  Toolbar,
  Item,
  Export,
  Selection,
  Scrolling,
  LoadPanel
} from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import Popup from 'devextreme-react/popup';
import Form from 'devextreme-react/form';
import { useUser } from '../../contexts/user';
import { checkPermission } from '../../permissions';
import { getApiToken } from '../../utils/authUtils';
import './competition-list.scss';

const courseOptions = [
  { value: 25, text: '25m' },
  { value: 50, text: '50m' },
  { value: 0, text: 'unbekannt' }
];

const initialFormState = {
  competitionName: '',
  city: '',
  course: 25,
  deadline: null
};

const createListStore = (token) => new CustomStore({
  key: 'competitionId',
  loadMode: 'raw',
  load: async () => {
    const response = await fetch(`${process.env.REACT_APP_serverEndpoint}/Competition/CompetitionsView`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const message = `Laden fehlgeschlagen (${response.status})`;
      throw new Error(message);
    }

    return response.json();
  }
});

const buildCreateFormItems = () => ([
  {
    dataField: 'competitionName',
    label: { text: 'Titel' },
    editorType: 'dxTextBox',
    isRequired: true,
    editorOptions: { stylingMode: 'filled', placeholder: 'Name des Wettkampfs' }
  },
  {
    dataField: 'city',
    label: { text: 'Stadt' },
    editorType: 'dxTextBox',
    editorOptions: { stylingMode: 'filled', placeholder: 'Ort' }
  },
  {
    dataField: 'course',
    label: { text: 'Bahn' },
    editorType: 'dxSelectBox',
    editorOptions: {
      items: courseOptions,
      valueExpr: 'value',
      displayExpr: 'text',
      stylingMode: 'filled'
    }
  },
  {
    dataField: 'deadline',
    label: { text: 'Meldeschluss' },
    editorType: 'dxDateBox',
    editorOptions: {
      type: 'datetime',
      displayFormat: 'dd.MM.yyyy HH:mm',
      stylingMode: 'filled'
    }
  }
]);

const CompetitionListPage = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const { permissions } = useUser();
  const gridRef = useRef(null);

  const [dataSource, setDataSource] = useState(null);
  const [createVisible, setCreateVisible] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [copying, setCopying] = useState(false);

  const canCreateCompetition = useMemo(() => {
    if (permissions && permissions.length > 0) {
      return permissions.includes('competition.create');
    }
    return checkPermission('competition.create');
  }, [permissions]);

  useEffect(() => {
    if (!canCreateCompetition && createVisible) {
      setCreateVisible(false);
    }
  }, [canCreateCompetition, createVisible]);

  const formItems = useMemo(buildCreateFormItems, []);

  useEffect(() => {
    getApiToken(instance, accounts)
      .then((token) => setDataSource(createListStore(token)))
      .catch(() => notify('Keine Berechtigung zum Laden der Wettkämpfe', 'error', 3000));
  }, [accounts, instance]);

  const refresh = useCallback(() => {
    gridRef.current?.instance?.refresh();
  }, []);

  const onOpen = useCallback((data) => {
    navigate(`/competition/${data.competitionId}`);
  }, [navigate]);

  const onCopy = useCallback(async (data) => {
    setCopying(true);
    try {
      const token = await getApiToken(instance, accounts);
      const payload = new FormData();
      payload.append('competitionId', data.competitionId);

      const response = await fetch(`${process.env.REACT_APP_serverEndpoint}/Competition/CopyCompetition`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: payload
      });

      if (response.status === 501) {
        const { error } = await response.json();
        notify(error || 'Kopieren ist noch nicht verfügbar', 'warning', 3000);
        return;
      }

      if (!response.ok) {
        const { error } = await response.json();
        throw new Error(error || 'Kopieren fehlgeschlagen');
      }

      notify('Wettkampf wurde kopiert', 'success', 2000);
      refresh();
    } catch (error) {
      notify(error.message || 'Kopieren fehlgeschlagen', 'error', 3000);
    } finally {
      setCopying(false);
    }
  }, [accounts, instance, refresh]);

  const onCreate = useCallback(async () => {
    if (!canCreateCompetition) {
      notify('Keine Berechtigung zum Anlegen', 'warning', 3000);
      setCreateVisible(false);
      return;
    }

    setSubmitting(true);
    try {
      const token = await getApiToken(instance, accounts);
      const payload = new FormData();
      const body = {
        competitionName: formData.competitionName,
        city: formData.city,
        course: formData.course ?? null,
        deadline: formData.deadline
      };

      payload.append('values', JSON.stringify(body));

      const response = await fetch(`${process.env.REACT_APP_serverEndpoint}/Competition/CreateCompetition`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: payload
      });

      if (!response.ok) {
        const { error } = await response.json();
        throw new Error(error || 'Anlegen fehlgeschlagen');
      }

      notify('Wettkampf wurde angelegt', 'success', 2000);
      setCreateVisible(false);
      setFormData(initialFormState);
      refresh();
    } catch (error) {
      notify(error.message || 'Anlegen fehlgeschlagen', 'error', 3000);
    } finally {
      setSubmitting(false);
    }
  }, [accounts, canCreateCompetition, formData, instance, refresh]);

  const onOpenCreate = useCallback(() => {
    if (!canCreateCompetition) {
      notify('Keine Berechtigung zum Anlegen', 'warning', 3000);
      return;
    }
    setCreateVisible(true);
  }, [canCreateCompetition]);

  const actionsCellRender = useCallback((cell) => (
    <div className='action-buttons'>
      <Button
        hint='Öffnen'
        icon='arrowright'
        stylingMode='text'
        onClick={() => onOpen(cell.data)}
      />
      <Button
        hint='Kopieren'
        icon='copy'
        stylingMode='text'
        disabled={copying}
        onClick={() => onCopy(cell.data)}
      />
    </div>
  ), [copying, onCopy, onOpen]);

  return (
    <div className='view competition-list'>
      <div className='view-wrapper view-wrapper-competition-list list-page'>
        <DataGrid
          dataSource={dataSource}
          ref={gridRef}
          className='grid theme-dependent'
          showBorders
          hoverStateEnabled
          onRowDblClick={({ data }) => onOpen(data)}
          height='100%'
          remoteOperations={false}
        >
          <LoadPanel showPane={false} />
          <SearchPanel visible placeholder='Wettkampf suchen' />
          <Export enabled formats={['xlsx', 'pdf', 'csv']} allowExportSelectedData />
          <Selection mode='single' />
          <Scrolling mode='standard' />

          <Toolbar>
            <Item location='before'>
              <div className='grid-header'>Wettkämpfe</div>
            </Item>
            {canCreateCompetition && (
              <Item location='after'>
                <Button
                  icon='plus'
                  text='Neu'
                  type='default'
                  stylingMode='contained'
                  onClick={onOpenCreate}
                />
              </Item>
            )}
            <Item location='after'>
              <Button
                icon='refresh'
                text='Aktualisieren'
                stylingMode='text'
                onClick={refresh}
              />
            </Item>
            <Item location='after' locateInMenu='auto'>
              <div className='separator' />
            </Item>
            <Item name='searchPanel' locateInMenu='auto' />
          </Toolbar>

          <Column dataField='startDate' caption='Beginn' dataType='date' format='dd.MM.yy' width={90} sortOrder='desc' />
          <Column dataField='endDate' caption='Ende' dataType='date' format='dd.MM.yy' width={90} />
          <Column dataField='competitionName' caption='Titel' dataType='string' minWidth={200} />
          <Column dataField='competitionNameShortUnique' caption='Kürzel' dataType='string' width={140} />
          <Column dataField='city' caption='Stadt' dataType='string' width={120} />
          <Column dataField='course' caption='Bahn' dataType='number' width={70} />
          <Column dataField='typ' caption='Typ' dataType='number' width={70} />
          <Column dataField='deadline' caption='Meldeschluss' dataType='date' format='dd.MM.yy HH:mm' width={150} />
          <Column dataField='timekeeping' caption='Zeitnahme' dataType='string' minWidth={120} />
          <Column dataField='hostName' caption='Ausrichter' dataType='string' minWidth={150} />
          <Column dataField='organizerName' caption='Veranstalter' dataType='string' minWidth={150} />
          <Column caption='Aktionen' width={150} cellRender={actionsCellRender} allowExporting={false} />
        </DataGrid>
      </div>

      {canCreateCompetition && (
        <Popup
          visible={createVisible}
          dragEnabled
          onHiding={() => setCreateVisible(false)}
          showCloseButton
          showTitle
          title='Neuer Wettkampf'
          width={520}
        >
          <div className='create-form'>
            <Form
              formData={formData}
              items={formItems}
              labelLocation='top'
              onFieldDataChanged={(e) => setFormData({ ...formData, [e.dataField]: e.value })}
            />
            <div className='popup-actions'>
              <Button
                text='Abbrechen'
                stylingMode='outlined'
                onClick={() => setCreateVisible(false)}
              />
              <Button
                text='Speichern'
                type='default'
                icon='check'
                disabled={submitting || !formData.competitionName}
                onClick={onCreate}
                loadingIndicatorEnabled={submitting}
              />
            </div>
          </div>
        </Popup>
      )}
    </div>
  );
};

export default CompetitionListPage;
